import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { EMPTY_PLACEHOLDER, extractDBdateString } from '@/common/utils/app.utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export function formatDateToYearMonth(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`;

  return `${year}-${month.padStart(2, '0')}`;
}

export function isValidPeriodString(period: string | null | undefined) {
  if (!period || period.length !== 6) {
    return false;
  }

  const [year, month] = extractDBdateString(period);

  if (isNaN(year) || year < 0) {
    return false;
  }

  if (isNaN(month) || month < 1 || month > 12) {
    return false;
  }

  return true;
}

export function isValidDbDateString(dbDate: string) {
  const [year, month] = extractDBdateString(dbDate);

  const date = new Date(year, month - 1, 1);
  const dateMonth = date.getMonth() + 1;
  const dateYear = date.getFullYear();

  if (dateMonth === month && year === dateYear) {
    return date;
  }

  return false;
}

export function isValidDateString(dateString: string) {
  const dateParts = dateString.split('-');

  const year = Number(dateParts[0]);
  const month = Number(dateParts[1]);
  const day = Number(dateParts[2]);

  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
}

export function pastDate(yearsAgo: number) {
  const date = new Date();
  date.setFullYear(date.getFullYear() - yearsAgo);

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

export function isValidMinimumAge(dob: string, minimumAge: number) {
  const dateParts = dob.split('-');
  const year = Number(dateParts[0]);
  const month = Number(dateParts[1]);
  const day = Number(dateParts[2]);
  const today = new Date();

  let age = today.getFullYear() - year;

  // * If the birthday hasn't happened yet, subtract 1 from the age
  if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) {
    age--;
  }

  return age >= minimumAge;
}

export function formatDateInDateTimeFormat(date: string) {
  const dateObj = date.split('T');
  const dateStr = dateObj[0];
  const timeObj = dateObj[1].split(':');
  const hours = timeObj[0];
  const minutes = timeObj[1];

  return `${dateStr} ${hours}:${minutes}`;
}

export function formatDateWithoutHyphens(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

export function formatDateWithHyphens(dateString: string, separator = '/'): string {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  const date = formatDbDateToReadable(dateString);

  if (!isValidDateString(date) || dateString.length !== 8) {
    throw new Error('Invalid date string');
  }

  return `${year}${separator}${month}${separator}${day}`;
}

// expected dateString: 20220221
export function formatDbDateToReadable(dateString: string, separator = '-') {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  return `${year}${separator}${month}${separator}${day}`;
}

// expected dateString: 202202
export function formatStringToYearMonth(dateString: string, separator = '-') {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);

  return `${year}${separator}${month}`;
}
export function timestampToDateTime(timestamp: string) {
  if (timestamp && typeof timestamp === 'string') {
    const tokens = timestamp.split(/T|:/);
    if (tokens.length < 4) {
      return undefined;
    }

    return `${tokens[0]} ${tokens[1]}:${tokens[2]}`;
  }
  return undefined;
}

// expects period: 202309
export function getDateRangeFromPeriod(startPeriod: string, endPeriod: string) {
  if (startPeriod.length !== 6 || endPeriod.length !== 6) {
    return {
      start: undefined,
      end: undefined,
    };
  }

  const start = formatDbDateToReadable(startPeriod + '01');

  const year = endPeriod.substring(0, 4);
  const month = endPeriod.substring(4, 6) as keyof typeof monthByDays;
  const days =
    (month === '02' ? (isLeapYear(Number(year)) ? '29' : '28') : monthByDays[month]) ??
    monthByDays['04'];
  const end = formatDbDateToReadable(endPeriod + days);
  return {
    start,
    end,
  };
}

export function formatEmployeeDateOfBirth(employeeDateOfBirthString: string): string {
  const date = formatDbDateToReadable(employeeDateOfBirthString);

  if (isValidDateString(date) && employeeDateOfBirthString.length === 8) {
    return formatDateWithHyphens(employeeDateOfBirthString);
  } else {
    return EMPTY_PLACEHOLDER;
  }
}

export function getYearFromPeriod(period: string) {
  const year = period.substring(0, 4);
  const month = period.substring(4, 6);

  if (['09', '10', '11', '12'].includes(month)) {
    return year;
  }

  return Number(year) - 1;
}

export function getCurrentMtDateTimeString() {
  return `${new Date().toLocaleString('en-CA', {
    timeZone: 'America/Edmonton',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  })} MT`;
}

function isLeapYear(year: number) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const monthByDays = {
  '01': 31,
  '02': 29,
  '03': 31,
  '04': 30,
  '05': 31,
  '06': 30,
  '07': 31,
  '08': 31,
  '09': 30,
  '10': 31,
  '11': 30,
  '12': 31,
};

/**
 * Returns all 12 months of the school year (September through August)
 * that the given month belongs to.
 *
 * @param input - A year-month string in "YYYYMM" format (e.g. "202609")
 * @returns An array of 12 "YYYYMM" strings from September to August
 *
 * @example
 * getSchoolYearMonths("202609") // ["202609", "202610", ..., "202708"]
 * getSchoolYearMonths("202701") // ["202609", "202610", ..., "202708"]
 */
export function getSchoolYearMonths(input: string): string[] {
  const year = parseInt(input.slice(0, 4), 10);
  const month = parseInt(input.slice(4, 6), 10);

  const startYear = month >= 9 ? year : year - 1;
  const endYear = startYear + 1;

  return [
    `${startYear}09`,
    `${startYear}10`,
    `${startYear}11`,
    `${startYear}12`,
    `${endYear}01`,
    `${endYear}02`,
    `${endYear}03`,
    `${endYear}04`,
    `${endYear}05`,
    `${endYear}06`,
    `${endYear}07`,
    `${endYear}08`,
  ];
}

/**
 * Convert timestamp to date format
 *
 * @param input - 2026-04-22T08:08:12.2410933
 * @returns 2026-04-22
 */
export function convertTimestampToDate(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculates the next sequential calendar period string, handling year-end rollovers.
 * For example, passing December ("12") will correctly increment the year and reset the month to January ("01").
 *
 * @param period - The current period string in "YYYYMM" format (e.g., "202312").
 * @returns The next calendar period string in "YYYYMM" format.
 *
 * @example
 * ```typescript
 * getNextCalendarPeriod("202009");
 * // returns "202010"
 * getNextCalendarPeriod("202312");
 * // returns "202401"
 * ```
 */
export function getNextCalendarPeriod(period: string): string {
  // Extract year and month as numbers
  const year = parseInt(period.substring(0, 4), 10);
  const month = parseInt(period.substring(4, 6), 10);

  let nextYear = year;
  let nextMonth = month + 1;

  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear += 1;
  }

  const nextMonthStr = nextMonth.toString().padStart(2, '0');
  return `${nextYear}${nextMonthStr}`;
}
